import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import i18next from 'i18next'

import { error, success } from 'src/components/system_wide/notification'

import axios from 'src/lib/axios'

const { t } = i18next

const initialState = {
  total: 0,
  brands: [],
  show_form: false,
  errors: {},
  current_brand: {
    id: null,
    name: '',
    business_name: '',
    pec: '',
    email: '',
    piva: '',
    cf: '',
    cuu: '',
    address: '',
    city: '',
    province: '',
    region: '',
    cap: '',
    country: '',
    lat: '',
    lng: '',
    place_id: '',
    enabled: true,
    references: [],
    brand_img: '',
  },
  all_brands: [],
}

export const getBrands = createAsyncThunk(
  'brands/list',
  async ({ offset, limit, sortData, filter, filtered_statuses }, thunkApi) => {
    try {
      let { total: currentTotal } = thunkApi.getState().brands
      if (offset > currentTotal) return

      let { sort_column, sort_direction } = sortData
      let response = await axios({
        url: `/api/brands`,
        params: { offset, limit, sort_column, sort_direction, filter, filtered_statuses: filtered_statuses.join(',') },
      })
      let { total, brands } = response.data
      return { total, brands, reset: offset === 0 }
    } catch (err) {
      error(t('store.brands.get_brands_error', 'Impossibile recuperare lista brand'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const getAllBrands = createAsyncThunk('brands/all', async (user_id, thunkApi) => {
  try {
    let response = await axios({ url: `/api/brands/all`, method: 'GET', params: { user_id } })
    return response.data.brands
  } catch (err) {
    error(t('store.brands.get_brands_error', 'Impossibile recuperare lista brand'))
    return thunkApi.rejectWithValue()
  }
})

export const createBrand = createAsyncThunk('brands/create', async ({ brand }, thunkApi) => {
  try {
    await axios({
      url: `/api/brands`,
      method: 'post',
      data: { ...brand },
    })
    success(t('store.brands.create_brand_success', 'Brand creato con successo'))
    thunkApi.dispatch(resetCurrentBrand())
    thunkApi.dispatch(toggleShowForm())
  } catch (err) {
    return thunkApi.rejectWithValue(err?.response?.data ?? {})
  }
})

export const updateBrand = createAsyncThunk('brands/update', async ({ brand }, thunkApi) => {
  try {
    await axios({
      url: `/api/brands/${brand.id}`,
      method: 'patch',
      data: { ...brand },
    })
    success(t('store.brands.update_brand_success', 'Brand aggiornato con successo'))
    thunkApi.dispatch(resetCurrentBrand())
    thunkApi.dispatch(toggleShowForm())
  } catch (err) {
    return thunkApi.rejectWithValue(err?.response?.data ?? {})
  }
})

export const deleteBrand = createAsyncThunk('brands/delete', async ({ id }, thunkApi) => {
  try {
    await axios({ url: `/api/brands/${id}`, method: 'delete' })
    success(t('store.brands.delete_brand_success', 'Brand eliminato con successo'))
    thunkApi.dispatch(resetCurrentBrand())
  } catch (err) {
    error(t('store.brands.delete_brand_error', 'Impossibile eliminare Brand'))
    return thunkApi.rejectWithValue()
  }
})

export const brandsSlice = createSlice({
  name: 'brands',
  initialState,

  reducers: {
    setCurrentBrand: (state, action) => {
      let {
        id,
        name,
        business_name,
        pec,
        email,
        piva,
        cf,
        cuu,
        address,
        city,
        province,
        region,
        cap,
        country,
        lat,
        lng,
        place_id,
        enabled,
        references,
        brand_img,
      } = action.payload

      state.current_brand.id = id
      state.current_brand.name = name
      state.current_brand.business_name = business_name
      state.current_brand.email = email
      state.current_brand.pec = pec
      state.current_brand.piva = piva
      state.current_brand.cf = cf
      state.current_brand.cuu = cuu
      state.current_brand.address = address
      state.current_brand.city = city
      state.current_brand.province = province
      state.current_brand.region = region
      state.current_brand.cap = cap
      state.current_brand.country = country
      state.current_brand.lat = lat
      state.current_brand.lng = lng
      state.current_brand.place_id = place_id
      state.current_brand.enabled = enabled
      state.current_brand.brand_img = brand_img
      state.current_brand.references = (references ?? []).map((r, index) => ({ id: index + 1, ...r }))
    },
    updateErrors: (state, action) => {
      state.errors = action.payload.errors
    },
    toggleShowForm: state => {
      state.show_form = !state.show_form
    },
    resetCurrentBrand: state => {
      state.current_brand = initialState.current_brand
    },
    resetBrands: () => initialState,
  },

  extraReducers: {
    [createBrand.rejected]: (state, action) => {
      state.errors = action.payload
    },
    [updateBrand.rejected]: (state, action) => {
      state.errors = action.payload
    },
    [getBrands.fulfilled]: (state, action) => {
      if (!action?.payload) return
      let { total, brands } = action.payload
      state.total = total
      state.brands = action.payload.reset ? [...brands] : [...state.brands, ...brands]
    },
    [getBrands.rejected]: state => {
      state.total = initialState.total
      state.brands = initialState.brands
    },
    [getAllBrands.fulfilled]: (state, action) => {
      state.all_brands = action.payload
    },
    [getAllBrands.rejected]: (state, action) => {
      state.all_brands = initialState.all_brands
    },
  },
})

export const { setCurrentBrand, updateErrors, resetCurrentBrand, toggleShowForm, resetBrands } = brandsSlice.actions
export default brandsSlice.reducer
